import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CtaButton } from "@/components/ui/CtaButton";
import { Plus, Trash2, GraduationCap, Layers, ArrowRight, X } from "lucide-react";
import { usePager, Pager } from "@/components/ui/TablePager";

/**
 * /teacher/grades — Top-level Grades manager.
 * Teacher adds Grades FIRST. Subjects can then be attached to a Grade on the
 * Subjects page.
 */
export default function Grades() {
  const [grades, setGrades] = useState([]);
  const [subjectCounts, setSubjectCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const [g, s] = await Promise.all([api.get("/grades"), api.get("/subjects")]);
      setGrades(g.data.grades || []);
      const counts = {};
      (s.data.subjects || []).forEach((su) => { if (su.grade_id) counts[su.grade_id] = (counts[su.grade_id] || 0) + 1; });
      setSubjectCounts(counts);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this grade? Subjects linked to it will be detached but kept.")) return;
    try { await api.delete(`/grades/${id}`); reload(); toast.success("Grade removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const filtered = grades
    .filter((g) => !q || (g.name + " " + (g.description || "")).toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const { page, setPage, pages, pageItems, total } = usePager(filtered, 25);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allChecked = filtered.length > 0 && filtered.every((g) => selected.includes(g.id));
  const toggleAll = () => setSelected(allChecked ? [] : filtered.map((g) => g.id));

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} grade${selected.length > 1 ? "s" : ""}? Linked subjects are detached but kept.`)) return;
    const res = await Promise.allSettled(selected.map((id) => api.delete(`/grades/${id}`)));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    toast.success(`Deleted ${ok} grade${ok === 1 ? "" : "s"}`);
    setSelected([]);
    reload();
  };

  return (
    <div className="space-y-6" data-testid="grades-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ grades</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">Grades</h1>
          <p className="text-sm text-neutral-500 mt-1">Add the grades / classes / levels you teach. Step 1 of setting up your curriculum.</p>
        </div>
        <CtaButton onClick={() => setShowAdd(true)} variant="primary" size="md" data-testid="add-grade-btn" className="rounded-md">
          <Plus size={14} /> Add grade
        </CtaButton>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : grades.length === 0 ? (
        <Empty onAdd={() => setShowAdd(true)} />
      ) : (
        <div className="bg-white border border-black/8 rounded-xl overflow-hidden" data-testid="grades-table">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-black/8 flex-wrap">
            <input
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search grades…"
              data-testid="grades-search"
              className="flex-1 min-w-[180px] max-w-xs text-sm border border-neutral-300 rounded-md px-3 py-2 outline-none focus:border-[#002FA7]"
            />
            {selected.length > 0 && (
              <button onClick={bulkDelete} data-testid="grades-bulk-delete"
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-md bg-red-500/10 text-red-500 border border-red-400/40 hover:bg-red-500/20">
                <Trash2 size={12} /> Delete selected ({selected.length})
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FBFAF7] text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="px-4 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="grades-select-all" /></th>
                <th className="text-left px-5 py-3">Grade</th>
                <th className="text-left px-5 py-3">Description</th>
                <th className="text-left px-5 py-3">Subjects</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((g) => (
                <tr key={g.id} className="border-t border-black/5 hover:bg-[#FBFAF7]/50" data-testid={`grade-row-${g.id}`}>
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(g.id)} onChange={() => toggle(g.id)} data-testid={`grade-check-${g.id}`} /></td>
                  <td className="px-5 py-3 font-semibold inline-flex items-center gap-2">
                    <span className="w-8 h-8 rounded-md bg-gradient-to-br from-[#7C3AED]/15 to-[#FFB400]/15 inline-flex items-center justify-center"><GraduationCap size={14} className="text-[#7C3AED]" /></span>
                    {g.name}
                  </td>
                  <td className="px-5 py-3 text-neutral-600">{g.description || "—"}</td>
                  <td className="px-5 py-3 text-neutral-500">
                    <span className="inline-flex items-center gap-1 text-[#0A0C10]">
                      <Layers size={12} /> {subjectCounts[g.id] || 0}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/teacher/subjects?grade=${g.id}`} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline mr-3">View subjects <ArrowRight size={11} /></Link>
                    <button onClick={() => onDelete(g.id)} data-testid={`grade-del-${g.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pages={pages} total={total} onPage={setPage} testid="grades-pager" />
        </div>
      )}

      {showAdd && <GradeModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reload(); }} />}
    </div>
  );
}

function Empty({ onAdd }) {
  return (
    <div className="bg-white border border-dashed themed-border rounded-md p-12 text-center" data-testid="grades-empty">
      <div className="w-12 h-12 mx-auto rounded-md bg-[#0A0C10] flex items-center justify-center"><GraduationCap size={22} className="text-[#FBFAF7]" /></div>
      <h3 className="mt-4 font-display font-bold text-xl tracking-tighter">No grades yet</h3>
      <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">Step 1 — add the grades you teach (Grade 8, O-Level, Class V…). Then head to Subjects to attach subjects under each grade.</p>
      <CtaButton onClick={onAdd} variant="primary" size="md" className="mt-5 rounded-md"><Plus size={14} /> Add your first grade</CtaButton>
    </div>
  );
}

function GradeModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    // Split on comma or newline so a teacher can add "Grade 5, Grade 6, Grade 7"
    // or one-per-line in a single go. Empty tokens are dropped.
    const names = name
      .split(/[,\n]/)
      .map((n) => n.trim())
      .filter(Boolean);
    if (names.length === 0) return toast.error("Grade name required");
    setBusy(true);
    try {
      // Description is shared across every grade in the batch — teacher can
      // still edit each grade individually afterwards.
      const results = await Promise.allSettled(
        names.map((n) => api.post("/grades", { name: n, description: desc.trim() }))
      );
      const okCount = results.filter((r) => r.status === "fulfilled").length;
      const failCount = results.length - okCount;
      if (okCount > 0) {
        toast.success(okCount === 1 ? "Grade added" : `Added ${okCount} grades${failCount ? ` · ${failCount} skipped` : ""}`);
        onSaved();
      } else {
        toast.error("Could not add any grade — names may already exist");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg p-6 border themed-border shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="grade-modal">
        <div className="flex items-center justify-between mb-5 pb-4 border-b themed-border">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ grades</div>
            <h3 className="font-display font-bold text-xl tracking-tighter mt-0.5">Add grade</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10] transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Name(s) *</div>
            <textarea rows={2} className="w-full bg-white text-sm border themed-border rounded-md px-3 py-2 outline-none focus:border-[#0A0C10]" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grade 9, Grade 10, Grade 11" data-testid="grade-name-input" />
            <div className="text-[10px] font-mono text-neutral-400 mt-1">Tip · separate multiple grades with commas or new lines</div>
          </label>
          <label className="block">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1.5">Description (optional)</div>
            <textarea rows={3} className="w-full bg-white text-sm border themed-border rounded-md px-3 py-2 outline-none focus:border-[#0A0C10]" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Stage, board, age group…" data-testid="grade-desc-input" />
          </label>
          <CtaButton type="submit" disabled={busy} variant="primary" size="lg" className="w-full rounded-md" data-testid="grade-submit">
            <Plus size={14} /> {busy ? "Saving…" : "Create grade(s)"}
          </CtaButton>
        </form>
      </div>
    </div>
  );
}
